import { useEffect, useRef, useState } from "react";
import axios from "axios";
import DailyIframe from "@daily-co/daily-js";
import { Loader2, Mic, MicOff, Radio } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-only mic broadcast: when ON, every logged-in user hears the admin in
 * real time and sees the "Crisangel está hablando en vivo" floating modal.
 */
export const AdminLiveMicSection = () => {
  const { authHeader } = useAuth();
  const [live, setLive] = useState(false);
  const [serverActive, setServerActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const callRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/live-audio/status`, { headers: authHeader() });
        if (!cancelled) setServerActive(!!data?.active);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanup = () => {
    try {
      callRef.current?.leave?.();
      callRef.current?.destroy?.();
    } catch { /* noop */ }
    callRef.current = null;
    // Also kill any stale Daily call instance that may have been left over
    // (e.g. by a previous failed start) — createCallObject throws if one
    // already exists, which surfaces as "No se pudo iniciar la transmisión".
    try {
      const existing = DailyIframe.getCallInstance?.();
      if (existing) {
        existing.leave?.();
        existing.destroy?.();
      }
    } catch { /* noop */ }
  };

  // Cleanup on unmount (broadcast keeps running server-side until Detener)
  useEffect(() => () => cleanup(), []);

  const start = async () => {
    setBusy(true);
    let backendOk = false;
    try {
      // 1) Make sure no stale call object is hanging around from a prior attempt.
      cleanup();

      // 2) Ask the browser for the microphone BEFORE we touch Daily, so we
      //    can surface a clear error if the user denied or the device has
      //    no mic. Daily's own error in this case is opaque ("getUserMedia
      //    error") which has been confusing admins.
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        // Release the test stream — Daily will request its own.
        stream.getTracks().forEach((t) => t.stop());
      } catch (permErr) {
        const name = permErr?.name || "";
        const msg = name === "NotAllowedError" || name === "PermissionDeniedError"
          ? "El navegador bloqueó el micrófono. Permite el acceso en el candado de la URL y vuelve a intentar."
          : name === "NotFoundError" || name === "DevicesNotFoundError"
            ? "No se detectó ningún micrófono conectado."
            : `No se pudo acceder al micrófono: ${permErr?.message || name || "error desconocido"}`;
        toast.error(msg);
        setBusy(false);
        return;
      }

      // 3) Create the Daily room + owner token on the backend.
      const { data } = await axios.post(`${API}/admin/live-audio/start`, {}, { headers: authHeader() });
      backendOk = true;

      // 4) Join the room as broadcaster.
      const call = DailyIframe.createCallObject({
        audioSource: true,
        videoSource: false,
        subscribeToTracksAutomatically: false, // admin doesn't need to hear listeners
      });
      callRef.current = call;
      await call.join({
        url: data.room_url,
        token: data.token,
        startVideoOff: true,
        startAudioOff: false,
      });
      await call.setLocalAudio(true);
      setLive(true);
      setServerActive(true);
      toast.success("🎙️ Micrófono EN VIVO — todos los usuarios te escuchan ahora");
    } catch (e) {
      cleanup();
      // If the backend already created the room, free it so we don't leak
      // a ghost "active" state where users see the overlay with no audio.
      if (backendOk) {
        try { await axios.post(`${API}/admin/live-audio/stop`, {}, { headers: authHeader() }); } catch { /* noop */ }
      }
      // Log the real error so production issues are diagnosable.
      // eslint-disable-next-line no-console
      console.error("[admin-live-mic] start failed:", e);
      const backendDetail = e?.response?.data?.detail;
      const clientMsg = e?.message;
      toast.error(
        backendDetail
          || (clientMsg ? `No se pudo iniciar el audio: ${clientMsg}` : "No se pudo iniciar la transmisión de audio")
      );
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/live-audio/stop`, {}, { headers: authHeader() });
    } catch { /* best effort */ }
    cleanup();
    setLive(false);
    setServerActive(false);
    setBusy(false);
    toast.success("Transmisión de audio finalizada");
  };

  return (
    <div
      className={`glass-dark rounded-2xl p-5 border ${live ? "border-red-500/50 shadow-[0_0_40px_rgba(239,68,68,0.2)]" : "border-white/10"}`}
      data-testid="admin-live-mic-section"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-lg text-white flex items-center gap-2">
            <Radio className={`w-5 h-5 ${live ? "text-red-400 animate-pulse" : "text-[#d4af37]"}`} />
            Micrófono en Vivo (toda la web)
          </h3>
          <p className="text-xs text-neutral-400 mt-1 max-w-lg">
            Al activarlo, <b className="text-[#d4af37]">todos los usuarios conectados</b> escuchan tu voz en tiempo
            real y les aparece una ventanita animada "Crisangel está hablando en vivo". Se cierra automáticamente
            cuando lo desactivas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {live && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-600 text-white text-[10px] uppercase tracking-widest font-bold">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" /> En vivo
            </span>
          )}
          {!live ? (
            <button
              data-testid="admin-mic-start"
              onClick={start}
              disabled={busy}
              className="btn-gold px-5 py-2.5 rounded-full text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
              Activar micrófono
            </button>
          ) : (
            <button
              data-testid="admin-mic-stop"
              onClick={stop}
              disabled={busy}
              className="px-5 py-2.5 rounded-full text-xs uppercase tracking-widest flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MicOff className="w-4 h-4" />}
              Detener
            </button>
          )}
        </div>
      </div>
      {serverActive && !live && (
        <div className="mt-3 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5" data-testid="admin-mic-orphan-warning">
          ⚠️ Hay una transmisión activa iniciada desde otra ventana/dispositivo. Puedes <b>Activar micrófono</b> para
          retomarla aquí, o presiona <button onClick={stop} className="underline text-amber-200">Detener transmisión</button>.
        </div>
      )}
    </div>
  );
};
